import { FC, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@hooks/useAppDispatch'
import { capitalize } from 'lodash-es'
import HorizontalSpinner from '@components/HorizontalSpinner'
import Button from '@components/Button'
import { fetchPermissionablePermissions, revokePermission } from '../actions'
import { resetState } from '../reducers'
import {
  selectPermissionsUI,
  selectDestroyablePermissionIds,
  selectPermissionsStatus,
  selectPermissionsError,
} from '../reducers'
import RequestStatus from '@constants/request_statuses'
import { PermissionUIRecord, DestroyablePermissionIds } from '../types'
import { SerializableError } from '@app-types/general'

type ResourcePermissionsProps = {
  resourceId: string | number
  resourceType: string
}

const ResourcePermissions: FC<ResourcePermissionsProps> = ({ resourceId, resourceType }) => {
  const dispatch = useAppDispatch()

  const permissionsUI: PermissionUIRecord = useSelector(selectPermissionsUI)
  const destroyablePermissionIds: DestroyablePermissionIds = useSelector(selectDestroyablePermissionIds)
  const status: RequestStatus = useSelector(selectPermissionsStatus)
  const error: SerializableError = useSelector(selectPermissionsError)

  useEffect(() => {
    dispatch(resetState())
    dispatch(fetchPermissionablePermissions({ resourceId, resourceType }))
  }, [resourceId, resourceType, dispatch])

  // Loading and error handling
  if (status === RequestStatus.LOADING) {
    return (
      <div className="p">
        <HorizontalSpinner size="large" />
      </div>
    )
  }

  if (status === RequestStatus.FAILED) {
    return <div className="p">Error: {error.message}</div>
  }

  const permissionArray = Object.values(permissionsUI)

  return (
    <div>
      <h3 className="zeta">Who has access:</h3>
      {permissionArray.length > 0 ? (
        <ul className="unstyled-list">
          {permissionArray.map(permission => (
            <li className="permission" key={permission.id}>
              <div className="permission__description">{capitalize(permission.readable_text)}</div>
              {destroyablePermissionIds.includes(permission.id) && (
                <Button onClick={() => dispatch(revokePermission(permission.id))}>Remove access</Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div>No users or groups have access.</div>
      )}
    </div>
  )
}

export default ResourcePermissions
